"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";
import { cva, type VariantProps } from "class-variance-authority";

const dialogSizeVariants = cva(
  "relative mx-auto flex max-h-[85vh] flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_4px_6px_-1px_rgba(0,0,0,0.04),0_18px_48px_-14px_rgba(15,23,42,0.09),0_6px_18px_-6px_rgba(0,0,0,0.06)] dark:border-zinc-800 dark:bg-zinc-900",
  {
    variants: {
      size: {
        sm: "w-full max-w-sm",
        md: "w-full max-w-md",
        lg: "w-full max-w-2xl",
        xl: "w-full max-w-4xl",
      },
    },
    defaultVariants: { size: "md" },
  }
);

export interface DialogProps extends VariantProps<typeof dialogSizeVariants> {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  className,
}: DialogProps) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  // ponytail: portal to body so the backdrop/modal escape any page stacking
  // context (e.g. #sidebar-v2 z-index:10050 would otherwise paint over it).
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  if (!open || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0"
        onClick={onClose}
        aria-hidden
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          dialogSizeVariants({ size }),
          "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-200",
          className
        )}
        style={{ zIndex: Z_INDEX.MODAL }}
      >
        {/* Header */}
        {(title || description) && (
          <div className="flex items-start justify-between border-b border-slate-200/70 p-5 dark:border-zinc-800">
            <div className="space-y-1">
              {title && (
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-slate-600 dark:text-zinc-400">
                  {description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-slate-200/70 p-5 dark:border-zinc-800">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
